import React, { useState, useEffect } from 'react';

import useApi from '../../../../hooks/useApi';
import {
  getStatisticsData,
  loadAllMetrics
} from '../../../../helpers/mainPageChartDataLoad';
import { calculateSocioDemoDeltas } from '../../../../helpers/calculateSocioDemoDeltas/calculateSocioDemoDeltas';
import getProfileAttributesFiltersLength from '../../../../helpers/getProfileAttributesFiltersLength';

import DeliveryPotential from './components/DeliveryPotential/DeliveryPotential';
import ChartMetric from './components/ChartMetric/ChartMetric';
import RepresentativeSample from './components/RepresentativeSample/RepresentativeSample';
import DeltaWarnings from './components/DeltaWarnings/DeltaWarnings';
import ProfileAttributesMetrics from './components/ProfileAttributesMetrics/ProfileAttributesMetrics';

import {
  FILTER_RELATIONS,
  MAIN_DATA_METRICS,
  REPRESENTATIVITY_OPTIONS,
  WARNINGS,
  SOCIO_DEMOGRAPHICS_CHARTS
} from '../../../../helpers/constants';

import styles from './MainPageCharts.module.css';

export default ({
  appliedFilters,
  filterRelation,
  isGlobalLoading,
  setIsGlobalLoading,
  totalSampleSize,
  totalSampleSizeLoading
}) => {
  const [statisticsData, setStatisticsData] = useState(getStatisticsData(null));
  const [api] = useApi();
  const [socioDemoDeltas, setSocioDemoDeltas] = useState(null);
  const [representativeSampleValue, setRepresentativeSampleValue] = useState(
    REPRESENTATIVITY_OPTIONS.RECOMMENDED
  );
  const [deltaWarnings, setDeltaWarnings] = useState([]);
  const [
    profileAttributesFiltersLength,
    setProfileAttributesFiltersLength
  ] = useState(getProfileAttributesFiltersLength(appliedFilters));

  useEffect(
    () => {
      const newProfileAttributesFiltersLength = getProfileAttributesFiltersLength(
        appliedFilters
      );
      if (
        newProfileAttributesFiltersLength !== profileAttributesFiltersLength
      ) {
        setProfileAttributesFiltersLength(newProfileAttributesFiltersLength);
      }

      // Empty values, loaders are shown
      setStatisticsData(getStatisticsData(null));

      // If no filters are configured basic request should have OR filterRelation
      loadAllMetrics(
        api,
        setStatisticsData,
        appliedFilters,
        !(appliedFilters && appliedFilters.length)
          ? FILTER_RELATIONS.OR
          : filterRelation
      );
    },
    [appliedFilters, filterRelation]
  );

  useEffect(
    () => {
      const isAnyMetricLoading = Object.entries(statisticsData).some(
        ([metricName, sD]) =>
          sD === null &&
          !(
            metricName === MAIN_DATA_METRICS.PROFILE_ATTRIBUTES &&
            profileAttributesFiltersLength === 0
          )
      );
      if (isAnyMetricLoading !== isGlobalLoading) {
        // eslint-disable-next-line
        setIsGlobalLoading(isGlobalLoading => isAnyMetricLoading);
      }
    },
    [statisticsData]
  );

  const updateSocioDemoDeltaState = chartDeltas => {
    const newDeltaWarnings = chartDeltas.reduce(
      (warningToBeDisplayed, chartDelta) => {
        if (chartDelta.deltaWarning) {
          return [...warningToBeDisplayed, chartDelta.deltaWarning];
        }
        return warningToBeDisplayed;
      },
      []
    );
    setDeltaWarnings(newDeltaWarnings);

    const newSocioDemoDeltas = chartDeltas.map(chart => ({
      deltaValues: chart.deltaValues,
      title: chart.title
    }));
    setSocioDemoDeltas(newSocioDemoDeltas);
  };

  useEffect(
    () => {
      if (statisticsData[MAIN_DATA_METRICS.SOCIO_DEMOGRAPHICS]) {
        const chartDeltas = Object.entries(
          statisticsData[MAIN_DATA_METRICS.SOCIO_DEMOGRAPHICS]
        ).map(([title, chartData]) =>
          calculateSocioDemoDeltas(
            appliedFilters,
            filterRelation,
            totalSampleSize,
            chartData.values,
            representativeSampleValue,
            title
          )
        );
        updateSocioDemoDeltaState(chartDeltas);
      }
    },
    [
      totalSampleSize,
      representativeSampleValue,
      statisticsData[MAIN_DATA_METRICS.SOCIO_DEMOGRAPHICS]
    ]
  );

  const getDeltaValues = (title, deltas) => {
    if (
      deltas &&
      deltas.length &&
      deltas.some(delta => delta.title === title)
    ) {
      const chartDelta = deltas.find(delta => delta.title === title);
      if (
        chartDelta &&
        chartDelta.deltaValues &&
        chartDelta.deltaValues.length &&
        chartDelta.deltaValues.some(delta => delta.yValue)
      ) {
        return chartDelta.deltaValues;
      }
    }
    return [];
  };

  const getWarnings = () => {
    let warnings = deltaWarnings.reduce((newWarnings, currentWarning) => {
      if (!newWarnings.includes(currentWarning)) {
        return [...newWarnings, currentWarning];
      }
      return newWarnings;
    }, []);

    if (
      !profileAttributesFiltersLength &&
      statisticsData[MAIN_DATA_METRICS.DELIVERY_POTENTIAL] &&
      statisticsData[MAIN_DATA_METRICS.DELIVERY_POTENTIAL] < totalSampleSize
    ) {
      warnings = [WARNINGS.SAMPLE_SIZE_NOT_FEASIBLE, ...warnings];
    }
    return warnings;
  };

  return (
    <div className={styles.container}>
      <DeliveryPotential
        totalSampleSize={totalSampleSize}
        maxDeliveryPotentialCount={
          statisticsData[MAIN_DATA_METRICS.DELIVERY_POTENTIAL] &&
          statisticsData[MAIN_DATA_METRICS.DELIVERY_POTENTIAL].deliveryPotential
        }
        totalSampleSizeLoading={totalSampleSizeLoading}
      />
      <DeltaWarnings warnings={getWarnings()} />
      <div className={styles.row}>
        <ProfileAttributesMetrics
          profileAttributesData={
            statisticsData[MAIN_DATA_METRICS.PROFILE_ATTRIBUTES]
          }
          profileAttributesFiltersLength={profileAttributesFiltersLength}
        />
      </div>
      <RepresentativeSample
        representativeSampleValue={representativeSampleValue}
        setRepresentativeSampleValue={setRepresentativeSampleValue}
      />
      <div className={styles.row}>
        <ChartMetric
          title={SOCIO_DEMOGRAPHICS_CHARTS.AGE}
          chartData={
            statisticsData[MAIN_DATA_METRICS.SOCIO_DEMOGRAPHICS] &&
            statisticsData[MAIN_DATA_METRICS.SOCIO_DEMOGRAPHICS][
              SOCIO_DEMOGRAPHICS_CHARTS.AGE
            ]
              ? {
                  ...statisticsData[MAIN_DATA_METRICS.SOCIO_DEMOGRAPHICS][
                    SOCIO_DEMOGRAPHICS_CHARTS.AGE
                  ],
                  deltaValues: getDeltaValues(
                    SOCIO_DEMOGRAPHICS_CHARTS.AGE,
                    socioDemoDeltas
                  )
                }
              : null
          }
          metricWidth={49}
          isSocioDemoChart
        />
        <ChartMetric
          title={SOCIO_DEMOGRAPHICS_CHARTS.GENDER}
          chartData={
            statisticsData[MAIN_DATA_METRICS.SOCIO_DEMOGRAPHICS] &&
            statisticsData[MAIN_DATA_METRICS.SOCIO_DEMOGRAPHICS][
              SOCIO_DEMOGRAPHICS_CHARTS.GENDER
            ]
              ? {
                  ...statisticsData[MAIN_DATA_METRICS.SOCIO_DEMOGRAPHICS][
                    SOCIO_DEMOGRAPHICS_CHARTS.GENDER
                  ],
                  deltaValues: getDeltaValues(
                    SOCIO_DEMOGRAPHICS_CHARTS.GENDER,
                    socioDemoDeltas
                  )
                }
              : null
          }
          metricWidth={49}
          isSocioDemoChart
        />
        <ChartMetric
          title={SOCIO_DEMOGRAPHICS_CHARTS.LANGUAGE}
          chartData={
            statisticsData[MAIN_DATA_METRICS.SOCIO_DEMOGRAPHICS] &&
            statisticsData[MAIN_DATA_METRICS.SOCIO_DEMOGRAPHICS][
              SOCIO_DEMOGRAPHICS_CHARTS.LANGUAGE
            ]
              ? {
                  ...statisticsData[MAIN_DATA_METRICS.SOCIO_DEMOGRAPHICS][
                    SOCIO_DEMOGRAPHICS_CHARTS.LANGUAGE
                  ],
                  deltaValues: getDeltaValues(
                    SOCIO_DEMOGRAPHICS_CHARTS.LANGUAGE,
                    socioDemoDeltas
                  )
                }
              : null
          }
          metricWidth={49}
          isSocioDemoChart
        />
      </div>
    </div>
  );
};
